import { useRef, KeyboardEvent, ClipboardEvent } from 'react'
import { clsx } from 'clsx'

interface OTPInputProps {
  length?: number
  value: string
  onChange: (value: string) => void
  error?: string
}

export default function OTPInput({ length = 6, value, onChange, error }: OTPInputProps) {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([])

  const digits = value.split('').concat(Array(length).fill('')).slice(0, length)

  const handleChange = (index: number, char: string) => {
    if (!/^\d*$/.test(char)) return
    const newDigits = [...digits] 
    newDigits[index] = char.slice(-1)
    onChange(newDigits.join(''))
    if (char && index < length - 1) {
      inputsRef.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    onChange(pasted.padEnd(length, '').slice(0, length))
    const nextIndex = Math.min(pasted.length, length - 1)
    inputsRef.current[nextIndex]?.focus()
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-3 justify-center">
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => { inputsRef.current[i] = el }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={i === 0 ? handlePaste : undefined}
            className={clsx(
              'w-12 h-14 text-center text-xl font-semibold rounded-lg border',
              'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
              'transition-colors duration-150 bg-background text-foreground',
              error ? 'border-destructive' : digit ? 'border-primary' : 'border-border'
            )}
          />
        ))}
      </div>
      {error && <p className="text-xs text-destructive text-center">{error}</p>}
    </div>
  )
}
